import cn from './cn.js';
import Skeleton from './Skeleton.js';

/**
 * Table primitives. Compose:
 * <Table>
 *   <THead><Tr><Th>Name</Th></Tr></THead>
 *   <TBody><Tr onClick=...><Td>…</Td></Tr></TBody>
 * </Table>
 */
export function Table({ className, wrapperClassName, children, dense = false, stickyHeader = false, bare = false }) {
  const table = (
    <table className={cn('min-w-full divide-y divide-gray-200', dense && 'text-xs', stickyHeader && '[&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10', className)}>
      {children}
    </table>
  );

  if (bare) return <div className={cn('overflow-x-auto', wrapperClassName)}>{table}</div>;

  return (
    <div className={cn('overflow-hidden rounded-2xl bg-white ring-1 ring-gray-200 shadow-card', wrapperClassName)}>
      <div className="overflow-x-auto">{table}</div>
    </div>
  );
}

export function THead({ className, children }) {
  return <thead className={cn('bg-gray-50', className)}>{children}</thead>;
}

export function TBody({ className, children }) {
  return <tbody className={cn('divide-y divide-gray-100 bg-white', className)}>{children}</tbody>;
}

export function Tr({ className, onClick, selected = false, children, ...props }) {
  return (
    <tr
      onClick={onClick}
      className={cn(
        'transition-colors duration-100',
        onClick && 'cursor-pointer hover:bg-gray-50',
        selected && 'bg-indigo-50/60',
        className,
      )}
      {...props}
    >
      {children}
    </tr>
  );
}

export function Th({ className, align = 'left', children, width, ...props }) {
  return (
    <th
      scope="col"
      style={width ? { width } : undefined}
      className={cn(
        'whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 first:pl-5 last:pr-5',
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left',
        className,
      )}
      {...props}
    >
      {children}
    </th>
  );
}

export function Td({ className, align = 'left', muted = false, mono = false, wrap = false, children, ...props }) {
  return (
    <td
      className={cn(
        'px-4 py-3 text-sm first:pl-5 last:pr-5',
        !wrap && 'whitespace-nowrap',
        muted ? 'text-gray-500' : 'text-gray-700',
        mono && 'font-mono text-xs',
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left',
        className,
      )}
      {...props}
    >
      {children}
    </td>
  );
}

export function TableEmpty({ colSpan = 1, children }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-5 py-10 text-center text-sm text-gray-500">
        {children}
      </td>
    </tr>
  );
}

export function TableSkeletonRows({ rows = 5, cols = 4 }) {
  return Array.from({ length: rows }).map((_, r) => (
    <tr key={`sk-${r}`}>
      {Array.from({ length: cols }).map((__, c) => (
        <td key={`sk-${r}-${c}`} className="px-4 py-3 first:pl-5 last:pr-5">
          <Skeleton className="h-4" style={{ width: `${55 + ((r * 7 + c * 13) % 40)}%` }} />
        </td>
      ))}
    </tr>
  ));
}
